import React, { useState } from 'react'
import {
    Box,
    Typography,
    Card,
    CardContent,
    Chip,
    IconButton,
    Button,
    Drawer,
    TextField,
    Stack,
    Divider,
    ToggleButton,
    ToggleButtonGroup,
    Autocomplete,
    Fab,
    Tooltip,
    TablePagination
} from '@mui/material'
import {
    Add as AddIcon,
    FilterList as FilterIcon,
    Close as CloseIcon,
    CalendarToday as CalendarIcon,
    FileUpload as FileUploadIcon,
    CheckCircle as CheckCircleIcon,
    Edit as EditIcon,
    Visibility as VisibilityIcon,
} from '@mui/icons-material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import BillingForm from './BillingForm/BillingForm'
import RemarkModal from './RemarkModal/RemarkModal'
import ImportBillingRecordsModal from './ImportBillingRecordsModal/ImportBillingRecordsModal'
import SolarPlantIcon from '../../../assets/images/solar plant_3.svg'
import Snackbar from '@mui/material/Snackbar'
import Alert from '@mui/material/Alert'
import usePermission from '../../../hooks/usePermission'

const BillingRecordsMobileView = ({
    records,
    loading,
    filters,
    setFilters,
    plantNames,
    organizations,
    statuses,
    // Actions
    handleAddRecord,
    handleEditRecord,
    handleViewRemark,
    handleOpenImportModal,
    // Modals state passed from parent if needed, or we can reuse the handlers which set parent state
    isFormOpen,
    handleCloseForm,
    handleSaveRecord,
    selectedRecord,
    formMode,
    isRemarkOpen,
    handleCloseRemark,
    importModalOpen,
    handleCloseImportModal,
    handleImportSuccess,
    importStatus,
    setImportStatus
}) => {
    const { can } = usePermission()
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [filterMode, setFilterMode] = useState('date') // 'date' or 'plant'

    // Filter Logic Handling locally for the drawer UI, syncing with parent filters
    const handleFilterChange = (key, value) => {
        setFilters({ ...filters, [key]: value })
    }

    // Determine status color for Card style
    const getStatusColor = (status) => {
        switch (status) {
            case 'Paid':
                return 'success'
            case 'Pending':
                return 'warning'
            case 'Unpaid':
                return 'error'
            default:
                return 'default'
        }
    }

    const getCardBorderColor = (status) => {
        if (status === 'To Be Verified') return '#FFC107' // Yellow
        if (status === 'Paid') return '#4CAF50' // Green
        return 'transparent'
    }

    // Mobile Filter Drawer Content
    const FilterDrawer = () => (
        <Box sx={{ p: 2, width: '85vw', maxWidth: 350 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Filters</Typography>
                <IconButton onClick={() => setFilterDrawerOpen(false)}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Stack spacing={2}>
                <Box display="flex" justifyContent="center" mb={1}>
                    <ToggleButtonGroup
                        value={filterMode}
                        exclusive
                        onChange={(e, val) => val && setFilterMode(val)}
                        size="small"
                    >
                        <ToggleButton value="date">
                            <CalendarIcon fontSize="small" sx={{ mr: 1 }} /> By Date
                        </ToggleButton>
                        <ToggleButton value="plant">
                            <img src={SolarPlantIcon} alt="plant" style={{ width: 18, height: 18, marginRight: 8 }} /> By Plant
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                {filterMode === 'plant' && (
                    <>
                        <Autocomplete
                            size="small"
                            options={plantNames}
                            value={filters.plantName || ''}
                            onChange={(event, newValue) => handleFilterChange('plantName', newValue)}
                            renderInput={(params) => (
                                <TextField {...params} label="Select Plant" placeholder="Plant Name" />
                            )}
                        />
                        <Autocomplete
                            size="small"
                            options={organizations}
                            value={filters.organization || ''}
                            onChange={(event, newValue) => handleFilterChange('organization', newValue)}
                            renderInput={(params) => (
                                <TextField {...params} label="Select Organization" placeholder="Organization" />
                            )}
                        />
                        <Autocomplete
                            size="small"
                            options={statuses}
                            value={filters.status || ''}
                            onChange={(event, newValue) => handleFilterChange('status', newValue)}
                            renderInput={(params) => (
                                <TextField {...params} label="Select Status" placeholder="Status" />
                            )}
                        />
                    </>
                )}

                {filterMode === 'date' && (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Filter by Month"
                            views={['month', 'year']}
                            value={filters.date}
                            onChange={(date) => handleFilterChange('date', date)}
                            slotProps={{ textField: { size: 'small', fullWidth: true } }}
                        />
                    </LocalizationProvider>
                )}

                <Button
                    variant="contained"
                    fullWidth
                    onClick={() => setFilterDrawerOpen(false)}
                    sx={{ mt: 2 }}
                >
                    Apply Filters
                </Button>
            </Stack>
        </Box>
    )

    return (
        <Box sx={{ pb: 10, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
            {/* Mobile Header */}
            <Box sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1100,
                bgcolor: 'white',
                p: 2,
                boxShadow: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Typography variant="h6" fontWeight="bold">Billing Records ({records.length})</Typography>
                <IconButton onClick={() => setFilterDrawerOpen(true)}>
                    <FilterIcon color="primary" />
                </IconButton>
            </Box>

            {/* Records List */}
            <Box sx={{ p: 2 }}>
                {records
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((record) => {
                        const isInvoiceGenerated = record.invoice && record.date;
                        const cardBorderColor = getCardBorderColor(record.status);

                        return (
                            <Card
                                key={record.id}
                                sx={{
                                    mb: 2,
                                    borderRadius: 2,
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                    position: 'relative',
                                    borderLeft: `6px solid ${cardBorderColor !== 'transparent' ? cardBorderColor : '#ccc'}`,
                                    overflow: 'visible'
                                }}
                            >
                                {/* Floating Status Chip on Top Right */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 12,
                                        right: 12,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5
                                    }}
                                >
                                    <Chip
                                        label={record.status}
                                        color={getStatusColor(record.status)}
                                        size="small"
                                        variant="filled"
                                        sx={{ fontWeight: 600, height: 24, fontSize: '0.75rem' }}
                                    />
                                    {isInvoiceGenerated && (
                                        <Tooltip title="Invoice Generated" arrow enterTouchDelay={0}>
                                            <CheckCircleIcon color="success" sx={{ fontSize: 20 }} />
                                        </Tooltip>
                                    )}
                                </Box>

                                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                    <Box pr={10}> {/* Padding right to avoid overlap with status chip */}
                                        <Typography variant="subtitle1" fontWeight="600" lineHeight={1.3}>
                                            {record.plantName}
                                            {record.capacity && (
                                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                    ({record.capacity} kWp)
                                                </Typography>
                                            )}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {dayjs(record.recordDate).format('MMM YYYY')}
                                        </Typography>
                                    </Box>

                                    <Divider sx={{ my: 1.5 }} />

                                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1} rowGap={2}>
                                        <Box>
                                            <Typography variant="caption" display="block" color="text.secondary">Act. Generation</Typography>
                                            <Typography variant="body2" fontWeight="500">
                                                {record.actualGeneration ? Number(record.actualGeneration).toFixed(0) : '-'} kWh
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" display="block" color="text.secondary">Amount</Typography>
                                            <Typography variant="body2" fontWeight="500">
                                                {record.amount ? `${record.amount} ${record.currency || 'INR'}` : '-'}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" display="block" color="text.secondary">Deemed Gen.</Typography>
                                            <Typography variant="body2" fontWeight="500">
                                                {record.deemedGeneration ? `${Number(record.deemedGeneration).toFixed(0)} kWh` : '-'}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" display="block" color="text.secondary">Tariff</Typography>
                                            <Typography variant="body2" fontWeight="500">
                                                {record.tariff ? record.tariff : '-'}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" display="block" color="text.secondary">Billed From</Typography>
                                            <Typography variant="body2" fontWeight="500">
                                                {record.billedFrom || '-'}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" display="block" color="text.secondary">Invoice No.</Typography>
                                            <Typography variant="body2" fontWeight="500">
                                                {record.invoice || '-'}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ gridColumn: 'span 2' }}>
                                            <Typography variant="caption" display="block" color="text.secondary">Organization</Typography>
                                            <Typography
                                                variant="body2"
                                                fontWeight="500"
                                                title={record.organization}
                                                sx={{
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    maxWidth: '100%'
                                                }}
                                            >
                                                {record.organization || '-'}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Box display="flex" justifyContent="flex-end" mt={1}>
                                        <IconButton size="small" onClick={() => handleViewRemark(record)}>
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>

                                        {can('billing.update') && (record.status === 'To Be Verified' || record.status === 'To Be Raised') && (
                                            <IconButton size="small" onClick={() => handleEditRecord(record)} color="primary">
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>

                                </CardContent>
                            </Card>
                        )
                    })}

                {records.length === 0 && (
                    <Box textAlign="center" mt={4} color="text.secondary">
                        <Typography>No records found.</Typography>
                    </Box>
                )}

                <TablePagination
                    component="div"
                    count={records.length}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10))
                        setPage(0)
                    }}
                    rowsPerPageOptions={[5, 10, 25]}
                    labelRowsPerPage="Rows:"
                    sx={{
                        borderTop: '1px solid #e0e0e0',
                        '& .MuiTablePagination-toolbar': {
                            px: { xs: 0.5, sm: 2 },
                            minHeight: 52,
                            width: '100%',
                            boxSizing: 'border-box',
                            flexWrap: { xs: 'nowrap', sm: 'nowrap' },
                            display: { xs: 'grid', sm: 'flex' },
                            gridTemplateColumns: { xs: 'auto 1fr auto', sm: 'unset' },
                            alignItems: 'center',
                        },
                        '& .MuiTablePagination-spacer': {
                            display: { xs: 'none', sm: 'block' },
                        },
                        '& .MuiTablePagination-selectLabel': {
                            display: { xs: 'none', sm: 'block' },
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            mb: 0,
                        },
                        '& .MuiTablePagination-select': {
                            fontSize: { xs: '0.72rem', sm: '0.875rem' },
                            mr: { xs: 0.25, sm: 2 },
                            lineHeight: 1,
                        },
                        '& .MuiTablePagination-selectRoot': {
                            mr: 0,
                            display: 'flex',
                            alignItems: 'center',
                            flexShrink: 0,
                            gridColumn: { xs: '1', sm: 'unset' },
                            '&::before': {
                                content: '"Rows:"',
                                display: { xs: 'inline-block', sm: 'none' },
                                fontSize: '0.75rem',
                                color: 'text.secondary',
                                mr: 0.5,
                                whiteSpace: 'nowrap',
                                lineHeight: 1,
                            },
                        },
                        '& .MuiTablePagination-displayedRows': {
                            fontSize: { xs: '0.72rem', sm: '0.875rem' },
                            ml: { xs: 0, sm: 'auto' },
                            mr: { xs: 0.25, sm: 1 },
                            whiteSpace: 'nowrap',
                            textAlign: 'right',
                            gridColumn: { xs: '2', sm: 'unset' },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            lineHeight: 1,
                        },
                        '& .MuiTablePagination-actions': {
                            ml: 0,
                            flexShrink: 0,
                            gridColumn: { xs: '3', sm: 'unset' },
                            display: 'flex',
                            alignItems: 'center',
                            '& .MuiIconButton-root': {
                                padding: { xs: '1px', sm: '8px' },
                            }
                        }
                    }}
                />
            </Box>

            {/* Floating Action Buttons */}
            <Box sx={{ position: 'fixed', bottom: 80, right: 16, zIndex: 1250, display: 'flex', flexDirection: 'row', gap: 2 }}>
                {can('billing.create') && (
                    <Fab
                        color="secondary"
                        aria-label="import"
                        onClick={handleOpenImportModal}
                        size="medium"
                        sx={{ bgcolor: '#fff', color: 'primary.main' }}
                    >
                        <FileUploadIcon />
                    </Fab>
                )}

                {can('billing.create') && (
                    <Fab
                        color="primary"
                        aria-label="add"
                        onClick={handleAddRecord}
                        size="medium"
                    >
                        <AddIcon />
                    </Fab>
                )}
            </Box>

            {/* Filter Drawer */}
            <Drawer
                anchor="right"
                open={filterDrawerOpen}
                onClose={() => setFilterDrawerOpen(false)}
            >
                <FilterDrawer />
            </Drawer>

            {/* Billing Form Dialog - Making it full screen on mobile essentially */}
            <BillingForm
                open={isFormOpen}
                onClose={handleCloseForm}
                onSave={handleSaveRecord}
                record={selectedRecord}
                mode={formMode}
            // Ensure the form component itself supports mobile or is responsive enough
            />

            {/* Remark Modal */}
            <RemarkModal
                open={isRemarkOpen}
                onClose={handleCloseRemark}
                record={selectedRecord}
            />

            {/* Import Modal */}
            <ImportBillingRecordsModal
                open={importModalOpen}
                onClose={handleCloseImportModal}
                onImportSuccess={handleImportSuccess}
            />

            <Snackbar
                open={importStatus.open}
                autoHideDuration={6000}
                onClose={() => setImportStatus({ ...importStatus, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setImportStatus({ ...importStatus, open: false })}
                    severity={importStatus.severity}
                    sx={{ width: '100%' }}
                    variant="filled"
                >
                    {importStatus.message}
                </Alert>
            </Snackbar>

        </Box>
    )
}

export default BillingRecordsMobileView
